import {
	MoreVert as MoreVertIcon,
	RemoveRedEyeOutlined,
	AssignmentTurnedInOutlined,
	ReceiptLongOutlined,
	AutorenewOutlined,
	CancelOutlined,
	PriceCheckOutlined,
} from "@mui/icons-material";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import React from "react";
import { useProfile } from "../../../context/ProfileContext";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { AgreementRenegotiationService, INegotiationBalance } from "../../../shared/services/api/sinister/AgreementRenegotiationService";
import { IAgreement } from "../../../shared/services/api/sinister/SinisterAgreementService";
import { ModalAgreementInstallments } from "./Modals/ModalAgreementInstallments";
import { ModalCancelAgreement } from "./Modals/ModalCancelAgreement";
import { ModalDefineAgencyTransferPlan } from "./Modals/ModalDefineAgencyTransferPlan";
import { ModalRenegotiateAgreement } from "./Modals/ModalRenegotiateAgreement";
import { ModalUpdateAgreementStatus } from "./Modals/ModalUpdateAgreementStatus";

interface Props {
	agreement: IAgreement;
	onChanged: () => void;
}

export function AgreementRowMenu({ agreement, onChanged }: Props) {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [openInstallmentsModal, setOpenInstallmentsModal] = React.useState(false);
	const [openStatusModal, setOpenStatusModal] = React.useState(false);
	const [openRenegotiateModal, setOpenRenegotiateModal] = React.useState(false);
	const [openCancelModal, setOpenCancelModal] = React.useState(false);
	const [openDefineTransferModal, setOpenDefineTransferModal] = React.useState(false);
	const [renegotiateBalance, setRenegotiateBalance] = React.useState<INegotiationBalance | null>(null);
	const open = Boolean(anchorEl);
	const { isSuperAdmin } = useProfile();
	const isLegal = agreement.sector === "juridico";
	const canUpdateStatus =
		isSuperAdmin ||
		checkByProfileGuard(
			PermissionRolesEnum.AgreementsOptions,
			isLegal ? PermissionActionsEnum.AlterAgreementStatusLegal : PermissionActionsEnum.AlterAgreementStatusCollection
		);
	const isAlreadyRenegotiated = agreement.status === "renegotiated";
	const canRenegotiate =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.RenegotiateAgreementLegal : PermissionActionsEnum.RenegotiateAgreementCollection
			)) &&
		!isAlreadyRenegotiated;
	const isAgreementCanceled = agreement.status === "broken";
	const canDefineTransferPlan =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.DefineAgencyTransferPlanLegal : PermissionActionsEnum.DefineAgencyTransferPlanCollection
			)) &&
		!isAgreementCanceled;

	const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);

	// Balance só é buscado quando o usuário realmente clica em "Renegociar" — não no mount da linha,
	// pra não gerar 1 fetch extra por acordo listado só por essa conveniência visual.
	const handleOpenRenegotiate = async () => {
		setOpenRenegotiateModal(true);
		setRenegotiateBalance(null);
		const negotiation = await AgreementRenegotiationService.findNegotiationBySinisterId(agreement.sinisterId);
		if (negotiation instanceof Error || !negotiation) return;
		const balanceResult = await AgreementRenegotiationService.getNegotiationBalance(negotiation.id);
		if (!(balanceResult instanceof Error)) setRenegotiateBalance(balanceResult);
	};

	return (
		<>
			<IconButton size="small" onClick={handleClick} aria-label="Ações">
				<MoreVertIcon fontSize="small" />
			</IconButton>

			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				<MenuItem
					onClick={() => {
						handleClose();
						window.open(`/sinistros/acordos/detalhe/${agreement.id}`, "_blank", "noopener");
					}}
				>
					<ListItemIcon>
						<RemoveRedEyeOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText>Ver detalhes do acordo</ListItemText>
				</MenuItem>

				<MenuItem
					onClick={() => {
						handleClose();
						setOpenInstallmentsModal(true);
					}}
				>
					<ListItemIcon>
						<ReceiptLongOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText>Ver Parcelas</ListItemText>
				</MenuItem>

				<Tooltip title={canUpdateStatus ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canUpdateStatus}
							onClick={() => {
								handleClose();
								setOpenStatusModal(true);
							}}
						>
							<ListItemIcon>
								<AssignmentTurnedInOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Alterar Status</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canUpdateStatus ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canUpdateStatus}
							onClick={() => {
								handleClose();
								setOpenCancelModal(true);
							}}
						>
							<ListItemIcon>
								<CancelOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Cancelar Acordo</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canRenegotiate ? "" : isAlreadyRenegotiated ? "Acordo já reparcelado" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canRenegotiate}
							onClick={() => {
								handleClose();
								handleOpenRenegotiate();
							}}
						>
							<ListItemIcon>
								<AutorenewOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Renegociar</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canDefineTransferPlan ? "" : isAgreementCanceled ? "Acordo cancelado" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canDefineTransferPlan}
							onClick={() => {
								handleClose();
								setOpenDefineTransferModal(true);
							}}
						>
							<ListItemIcon>
								<PriceCheckOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Repasse à Imobiliária</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>
			</Menu>

			<ModalAgreementInstallments open={openInstallmentsModal} agreementId={agreement.id} onClose={() => setOpenInstallmentsModal(false)} />

			<ModalRenegotiateAgreement
				open={openRenegotiateModal}
				agreement={agreement}
				balance={renegotiateBalance}
				onClose={() => setOpenRenegotiateModal(false)}
				onConfirm={() => {
					setOpenRenegotiateModal(false);
					onChanged();
				}}
			/>

			<ModalCancelAgreement
				open={openCancelModal}
				agreement={agreement}
				onClose={() => setOpenCancelModal(false)}
				onConfirm={() => {
					setOpenCancelModal(false);
					onChanged();
				}}
			/>

			<ModalUpdateAgreementStatus
				open={openStatusModal}
				agreement={agreement}
				onClose={() => setOpenStatusModal(false)}
				onConfirm={() => {
					setOpenStatusModal(false);
					onChanged();
				}}
			/>

			<ModalDefineAgencyTransferPlan
				open={openDefineTransferModal}
				agreementId={agreement.id}
				onClose={() => setOpenDefineTransferModal(false)}
				onConfirm={() => {
					setOpenDefineTransferModal(false);
					onChanged();
				}}
			/>
		</>
	);
}
